import ReactMarkdown from 'react-markdown';

import colors from 'atoms/colors/colors';

import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Paragraph from 'quarks/Paragraph';
import Text from 'quarks/Text';
import type { BasicProps } from 'quarks/interpolations/basic';

import Button, { ButtonProps } from 'molecules/Button/Button';

// import Link from 'molecules/Link';


import type { IComponentCardRecord } from 'graphql/types';
import type { FC } from 'react';

interface ResourcesCardProps extends BasicProps, IComponentCardRecord {
  backgroundIsDark: boolean;
  noHover: boolean;
  isSlider: boolean;
  cardBody?: IComponentCardRecord['bodyCopy'];
}

const ResourcesCard: FC<ResourcesCardProps> = ({
  resourcesCategories,
  button,
  cardBody,
  featuredMedia,
  headline,
  noHover,
  isSlider,
  isExternal,
  ...props
}) => (
  <a
    href={`${isExternal ? '' : 'https://startree.ai'}${button?.link}`}
    css={`
      text-decoration: none;
      color: ${colors.storm[900]};
    `}
  >
    <Flex
      flexDirection="column"
      width="100%"
      height="100%"
      maxWidth={isSlider ? '327px' : '100%'}
      minHeight={isSlider ? '383px' : 'auto'}
      backgroundColor="common-white"
      borderRadius="16px"
      border={`1px solid ${colors.storm[100]}`}
      textAlign="left"
      transition="all .3s"
      gap={isSlider ? '32px' : '0px'}
      md={{ maxWidth: isSlider ? '670px' : '370px', paddingAll: isSlider ? 40 : 0 }}
      lg={{ maxWidth: isSlider ? '670px' : '300px' }}
      xl={{ maxWidth: isSlider ? '670px' : '370px' }}
      css={{
        '&:hover': {
          transform: `${noHover ? 'translateY(0px)' : 'translateY(-10px)'}`,
        },
      }}
      {...props}
    >
      {featuredMedia && featuredMedia.length > 0 && (
        <Image
          width="100%"
          height="100%"
          borderTopLeftRadius="16px"
          borderTopRightRadius="16px"
          maxHeight="150px"
          src={featuredMedia[0].url}
          alt={featuredMedia[0].title as string}
          css={{ objectFit: 'cover' }}
        />
      )}
      <Flex height="100%" paddingAll={24} flexDirection="column">
        <Flex flexDirection="column" flexGrow={1} flexBasis={0} gap="16px" md={{ gap: isSlider ? '32px' : '16px' }}>
          {resourcesCategories && resourcesCategories.length > 0 && (
            <Paragraph css={{ wordBreak: 'break-all' }}>
              {resourcesCategories.map(cat => (
                <Text
                  borderRadius="16px"
                  backgroundColor="lightning-600"
                  paddingX={12}
                  paddingY={4}
                  fontSize="textXs"
                  marginRight={16}
                  fontWeight="semiBold"
                  whiteSpace="nowrap"
                  key={cat.id}
                >
                  {cat.tagTitle}
                </Text>
              ))}
            </Paragraph>
          )}
          <Heading
            as="h3"
            fontWeight="bold"
            textColor="storm-900"
            fontSize="textXl"
            lineHeight={isSlider ? 'sm' : 'sm'}
            overflowX="hidden"
            overflowY="hidden"
            md={{ fontSize: isSlider ? 'displaySm' : 'textXl' }}
            lg={{ fontSize: isSlider ? 'displayMd' : 'textXl' }}
            css={`
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
            `}
          >
            {headline}
          </Heading>
          {cardBody && (
            <Paragraph
              as="div"
              textStyle="md"
              textColor="storm-900"
              overflowX="hidden"
              overflowY="hidden"
              css={`
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
              `}
            >
              <ReactMarkdown>{cardBody || ''}</ReactMarkdown>
            </Paragraph>
          )}
        </Flex>
        <Flex
          flexDirection="column"
          alignItems="start"
          justifyContent="space-between"
          marginTop={16}
          gap="32px"
          md={{ flexDirection: isSlider ? 'row' : 'column', alignItems: isSlider ? 'center' : 'start' }}
        >
          {button && (
            <Button
              href={button?.link || ''}
              color={(button.variant as ButtonProps['color']) || 'primary'}
              size={(button.size as ButtonProps['size']) || 'md'}
              endIcon={button.endIcon}
            >
              {button.label}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  </a>
);

export default ResourcesCard;

ResourcesCard.defaultProps = {};
